import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http, money, mesi } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Sparkles, Wrench, FileText, ArrowDownLeft, ArrowUpRight, Shirt, Package, CheckSquare, AlertOctagon, Wallet, AlertCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import TodoBoard from "@/components/TodoBoard";
import { usePrivacy, togglePrivacy } from "@/lib/privacy";
import { Eye, EyeOff } from "lucide-react";

const now = new Date();

const QUICK = [
  { label: "Entrata", icon: ArrowDownLeft, to: "/incassi-pagamenti?new=income", cls: "bg-emerald-600" },
  { label: "Uscita", icon: ArrowUpRight, to: "/incassi-pagamenti?new=expense", cls: "bg-rose-600" },
  { label: "Pulizia", icon: Sparkles, to: "/pulizie?new=1", cls: "bg-sky-600" },
  { label: "Costo lavanderia", icon: Shirt, to: "/lavanderia?new=costo", cls: "bg-indigo-600" },
  { label: "Consumabile", icon: Package, to: "/consumabili?new=1", cls: "bg-amber-600" },
  { label: "Manutenzione", icon: Wrench, to: "/manutenzioni?new=1", cls: "bg-orange-600" },
  { label: "Todo", icon: CheckSquare, to: "/todo?new=1", cls: "bg-slate-600" },
  { label: "Rendiconto", icon: FileText, to: "/rendiconti?new=1", cls: "bg-[var(--teal-deep)]" },
];

function EconCard({ label, value, testid, strong }) {
  return (
    <div className={`rounded-2xl border p-4 ${strong ? "border-[var(--teal-medium)] bg-[color:var(--teal-light)]" : "border-[color:var(--pv-mint-soft)] bg-[color:var(--teal-light)]/50"}`} data-testid={testid}>
      <div className="text-[10px] sm:text-xs uppercase tracking-wider text-[var(--teal-medium)] font-semibold leading-tight">{label}</div>
      <div className="font-display text-xl md:text-2xl font-extrabold text-[var(--teal-deep)] mt-2 tabular-nums">{value}</div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(String(now.getMonth() + 1));
  const [apartmentId, setApartmentId] = useState("all");
  const [apartments, setApartments] = useState([]);
  const privacyOn = usePrivacy();
  const [ops, setOps] = useState(null);
  const [econ, setEcon] = useState(null);
  const [fin, setFin] = useState(null);

  useEffect(() => { http.get("/apartments").then((r) => setApartments(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    const yearMode = month === "all";
    const params = { year, month: yearMode ? 12 : Number(month) };
    if (apartmentId !== "all") params.apartment_id = apartmentId;
    http.get("/economics/summary", { params }).then((r) => setEcon(r.data)).catch(() => {});
    const fp = { year }; if (!yearMode) fp.month = Number(month); if (apartmentId !== "all") fp.apartment_id = apartmentId;
    http.get("/finance/summary", { params: fp }).then((r) => setFin(r.data)).catch(() => {});
    http.get("/dashboard/summary", { params: { year, month: yearMode ? now.getMonth() + 1 : Number(month), ...(apartmentId !== "all" ? { apartment_id: apartmentId } : {}) } }).then((r) => setOps(r.data)).catch(() => {});
  }, [year, month, apartmentId]);

  const yearMode = month === "all";
  const em = yearMode ? econ?.year_totals : econ?.month_totals;
  const periodLabel = yearMode ? `Anno ${year}` : `${mesi[Number(month)]} ${year}`;
  const isFinancialRole = user?.role === "admin" || user?.role === "collaboratore";
  const t = fin?.totals || {};
  const prov = fin?.provvigioni_ytd || {};
  const maturato = yearMode ? econ?.year_totals?.management_fee : econ?.ytd_totals?.management_fee;
  const totalePuravida = (maturato || 0) + (fin?.altri_ricavi_puravida || 0);
  const [ext, setExt] = useState(null);
  useEffect(() => {
    http.get("/other-activities", { params: { year, ...(yearMode ? {} : { month: Number(month) }) } })
      .then((r) => setExt(r.data)).catch(() => setExt(null));
  }, [year, month, yearMode]);
  const totaleGenerale = totalePuravida + (ext?.totale_esterni || 0);

  const ECON_CARDS = [
    { label: "Ricavi appartamenti", value: money(em?.total_net), testid: "econ-total-net", strong: true },
    { label: "Netto proprietari", value: money(em?.owner_result), testid: "econ-owner-result", strong: true },
    { label: "Provvigioni PuraVida", value: money(em?.management_fee), testid: "econ-fee", strong: true },
    { label: "Rendiconti finalizzati", value: em?.reports ?? 0, testid: "econ-reports", strong: true },
    { label: "Pulizie addebitate", value: money(em?.pulizie), testid: "econ-pulizie" },
    { label: "Lavanderia addebitata", value: money(em?.lavanderia), testid: "econ-lavanderia" },
    { label: "Manutenzioni addebitate", value: money(em?.manutenzioni), testid: "econ-manutenzioni" },
    { label: "Consumabili / amenities", value: money((em?.consumabili || 0) + (em?.amenities || 0)), testid: "econ-consumabili" },
    { label: "Altri costi", value: money(em?.altri), testid: "econ-altri" },
  ];

  const kpis = [
    { label: "Pulizie del mese", value: ops?.cleanings_count ?? 0, icon: Sparkles, color: "text-sky-700", bg: "bg-sky-50", testid: "kpi-cleanings", to: "/pulizie" },
    { label: "Pulizie non pagate", value: ops?.cleanings_unpaid ?? 0, icon: AlertCircle, color: "text-amber-700", bg: "bg-amber-50", testid: "kpi-cleanings-unpaid", to: "/pulizie" },
    { label: "Compensi da pagare", value: money(t.da_pagare), icon: Wallet, color: "text-rose-700", bg: "bg-rose-50", testid: "kpi-da-pagare", to: "/incassi-pagamenti", fin: true },
    { label: "Manutenzioni aperte", value: ops?.open_maintenance ?? 0, icon: Wrench, color: "text-orange-700", bg: "bg-orange-50", testid: "kpi-maintenance", to: "/manutenzioni" },
    { label: "Manutenzioni urgenti", value: ops?.urgent_maintenance ?? 0, icon: AlertOctagon, color: "text-rose-700", bg: "bg-rose-50", testid: "kpi-urgent", to: "/manutenzioni" },
    { label: "Todo scadute", value: ops?.overdue_tasks ?? 0, icon: AlertCircle, color: "text-rose-700", bg: "bg-rose-50", testid: "kpi-overdue", to: "/todo" },
    { label: "Rendiconti in bozza", value: ops?.reports_bozza ?? 0, icon: FileText, color: "text-amber-700", bg: "bg-amber-50", testid: "kpi-rep-bozza", to: "/rendiconti", fin: true },
  ].filter((k) => isFinancialRole || !k.fin);

  const series = (econ?.months || []).filter((m) => m.reports > 0);

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-[var(--terracotta)] font-semibold">Ciao {user?.name?.split(" ")[0]}</div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-[var(--teal-deep)]">Panoramica {periodLabel}</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger className="w-[140px]" data-testid="filter-month"><SelectValue /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tutto l'anno</SelectItem>{mesi.slice(1).map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-[100px]" data-testid="filter-year"><SelectValue /></SelectTrigger>
            <SelectContent>{[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={apartmentId} onValueChange={setApartmentId}>
            <SelectTrigger className="w-[180px]" data-testid="filter-apartment"><SelectValue placeholder="Appartamento" /></SelectTrigger>
            <SelectContent><SelectItem value="all">Tutti gli appartamenti</SelectItem>{apartments.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}</SelectContent>
          </Select>
          <button type="button" onClick={togglePrivacy} data-testid="privacy-toggle"
            title={privacyOn ? "Mostra importi" : "Nascondi importi"}
            aria-label={privacyOn ? "Mostra importi" : "Nascondi importi"}
            className={`h-11 w-11 shrink-0 rounded-xl border flex items-center justify-center transition-colors ${privacyOn ? "bg-[var(--teal-deep)] text-white border-[var(--teal-deep)]" : "bg-white text-slate-500 border-slate-200 hover:text-[var(--teal-deep)]"}`}>
            {privacyOn ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {isFinancialRole && (
        <section data-testid="econ-section" className="space-y-3">
          <div className="rounded-3xl p-5 md:p-7 text-white relative overflow-hidden" data-testid="banner-guadagni"
            style={{ background: "linear-gradient(120deg,#2E5C60 0%,#3F7B7C 55%,#1F4245 100%)" }}>
            <div className="absolute inset-0 grain" />
            <div className="relative z-10 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5">
              <div>
                <div className="text-[10px] sm:text-xs uppercase tracking-[0.2em] text-[color:var(--pv-mint-soft)] font-semibold">Totale PuraVida</div>
                <div className="font-display text-4xl sm:text-5xl font-extrabold tabular-nums mt-2" data-testid="banner-totale-puravida">{money(totalePuravida)}</div>
                <div className="text-xs sm:text-sm text-[color:var(--pv-mint-soft)] mt-1">{yearMode ? `Anno ${year}` : `${year} fino a ${mesi[Number(month)]}`} · valore economico maturato</div>
                <div className="flex gap-3 flex-wrap mt-3">
                  <div className="rounded-2xl bg-white/10 backdrop-blur px-4 py-2.5 min-w-[150px]">
                    <div className="text-[10px] uppercase tracking-wider text-[color:var(--pv-mint-soft)] font-semibold">Gestione PuraVida</div>
                    <div className="font-display text-2xl font-extrabold tabular-nums" data-testid="banner-year-value">{money(maturato)}</div>
                    <div className="text-[10px] text-[color:var(--pv-mint-soft)]">provvigioni dai rendiconti finalizzati</div>
                  </div>
                  <div className="rounded-2xl bg-white/10 backdrop-blur px-4 py-2.5 min-w-[150px]">
                    <div className="text-[10px] uppercase tracking-wider text-[color:var(--pv-mint-soft)] font-semibold">Altri ricavi / provvigioni PuraVida</div>
                    <div className="font-display text-2xl font-extrabold tabular-nums" data-testid="banner-altri-ricavi">{money(fin?.altri_ricavi_puravida)}</div>
                    <div className="text-[10px] text-[color:var(--pv-mint-soft)]">inseriti e inclusi nei risultati</div>
                  </div>
                </div>
              </div>
              <div className="flex gap-3 sm:gap-4 flex-wrap">
                {!yearMode && <div className="rounded-2xl bg-white/10 backdrop-blur px-4 py-3 min-w-[130px]">
                  <div className="text-[10px] uppercase tracking-wider text-[color:var(--pv-mint-soft)] font-semibold">Gestione questo mese</div>
                  <div className="font-display text-xl sm:text-2xl font-extrabold tabular-nums" data-testid="banner-month-value">{money(econ?.month_totals?.management_fee)}</div>
                </div>}
                <div className="rounded-2xl bg-emerald-400/20 backdrop-blur px-4 py-3 min-w-[130px]">
                  <div className="text-[10px] uppercase tracking-wider text-[color:var(--pv-mint-soft)] font-semibold">Incassate <span className="opacity-70">(cassa)</span></div>
                  <div className="font-display text-xl sm:text-2xl font-extrabold tabular-nums" data-testid="banner-incassate">{money(prov.incassato)}</div>
                </div>
                <div className="rounded-2xl bg-amber-300/20 backdrop-blur px-4 py-3 min-w-[130px]">
                  <div className="text-[10px] uppercase tracking-wider text-[color:var(--pv-mint-soft)] font-semibold">Da incassare <span className="opacity-70">(cassa)</span></div>
                  <div className="font-display text-xl sm:text-2xl font-extrabold tabular-nums" data-testid="banner-da-incassare">{money(prov.da_incassare)}</div>
                </div>
              </div>
            </div>
            <Link to="/analisi-economica" data-testid="banner-link" className="relative z-10 inline-flex mt-4 text-xs font-semibold text-white/90 underline underline-offset-4 hover:text-white">Apri Analisi economica →</Link>

            <div className="relative z-10 mt-5 pt-4 border-t border-white/20" data-testid="banner-altri-compensi">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--pv-mint-soft)] font-semibold">Altri compensi (fuori PuraVida)</div>
              <div className="flex gap-3 flex-wrap mt-2">
                <div className="rounded-2xl bg-white/10 backdrop-blur px-4 py-2.5 min-w-[140px]">
                  <div className="text-[10px] uppercase tracking-wider text-[color:var(--pv-mint-soft)] font-semibold">FAB SRL</div>
                  <div className="font-display text-xl font-extrabold tabular-nums" data-testid="banner-fab">{money(ext?.fab)}</div>
                </div>
                <div className="rounded-2xl bg-white/10 backdrop-blur px-4 py-2.5 min-w-[140px]">
                  <div className="text-[10px] uppercase tracking-wider text-[color:var(--pv-mint-soft)] font-semibold">Altri lavori</div>
                  <div className="font-display text-xl font-extrabold tabular-nums" data-testid="banner-altri-lavori">{money(ext?.altro)}</div>
                </div>
                <div className="rounded-2xl bg-[color:var(--pv-mint)]/25 backdrop-blur px-4 py-2.5 min-w-[170px]">
                  <div className="text-[10px] uppercase tracking-wider text-[color:var(--pv-mint-soft)] font-semibold">Totale generale</div>
                  <div className="font-display text-xl font-extrabold tabular-nums" data-testid="banner-totale-generale">{money(totaleGenerale)}</div>
                  <div className="text-[10px] text-[color:var(--pv-mint-soft)]">PuraVida + FAB + altri lavori</div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex items-baseline justify-between gap-3 pt-1">
            <h2 className="font-display text-lg md:text-lg font-bold text-[var(--teal-deep)]">Economia dai Rendiconti · {periodLabel}</h2>
            <span className="text-[11px] text-slate-500">Fonte: rendiconti finalizzati · bozze escluse</span>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">{ECON_CARDS.map((c) => <EconCard key={c.label} {...c} />)}</div>
        </section>
      )}

      {isFinancialRole && (
        <section className="space-y-3" data-testid="cash-section">
          <div className="flex items-baseline justify-between gap-3 pt-1">
            <h2 className="font-display text-lg md:text-lg font-bold text-slate-900">Cassa PuraVida · {periodLabel}</h2>
            <Link to="/incassi-pagamenti" className="text-[11px] font-semibold text-[var(--teal-deep)] underline underline-offset-4">Incassi &amp; Pagamenti →</Link>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="cash-da-incassare"><div className="text-[10px] sm:text-xs uppercase tracking-wider text-amber-800 font-semibold">Da incassare</div><div className="font-display text-xl md:text-2xl font-extrabold text-amber-900 mt-2 tabular-nums">{money(t.da_incassare)}</div><div className="text-[11px] text-amber-700">{t.count_da_incassare || 0} movimenti</div></div>
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4" data-testid="cash-incassato"><div className="text-[10px] sm:text-xs uppercase tracking-wider text-emerald-800 font-semibold">Incassato</div><div className="font-display text-xl md:text-2xl font-extrabold text-emerald-900 mt-2 tabular-nums">{money(t.incassato)}</div></div>
            <div className={`rounded-2xl border p-4 ${t.scaduto > 0 ? "border-rose-200 bg-rose-50" : "border-slate-200 bg-white"}`} data-testid="cash-scaduto"><div className="text-[10px] sm:text-xs uppercase tracking-wider text-slate-600 font-semibold">Scaduto</div><div className="font-display text-xl md:text-2xl font-extrabold text-slate-900 mt-2 tabular-nums">{money(t.scaduto)}</div></div>
          </div>
        </section>
      )}

      <TodoBoard />

      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="quick-actions">
        {QUICK.filter((q) => isFinancialRole || !["Entrata", "Uscita", "Rendiconto"].includes(q.label)).map((q) => (
          <Link key={q.label} to={q.to} data-testid={`quick-${q.label.toLowerCase().replace(/ /g, "-")}`}
            className={`${q.cls} text-white text-xs sm:text-sm font-semibold px-4 py-2.5 rounded-full whitespace-nowrap flex items-center gap-1.5 hover:brightness-110 min-h-[44px]`}>
            <q.icon className="h-4 w-4" /> + {q.label}
          </Link>
        ))}
      </div>

      <div className="flex items-baseline justify-between gap-3 pt-1">
        <h2 className="font-display text-lg md:text-lg font-bold text-slate-900">Operatività</h2>
        <span className="text-[11px] text-slate-500">Registri operativi reali</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {kpis.map((k) => (
          <Link to={k.to} key={k.label} className="kpi-card block" data-testid={k.testid}>
            <div className="flex items-start justify-between">
              <div className="text-[10px] sm:text-xs uppercase tracking-wider text-slate-500 font-semibold leading-tight">{k.label}</div>
              <div className={`${k.bg} p-1.5 rounded-lg shrink-0`}><k.icon className={`h-4 w-4 ${k.color}`} /></div>
            </div>
            <div className="font-display text-xl md:text-2xl font-extrabold text-slate-900 mt-2 tabular-nums">{k.value}</div>
          </Link>
        ))}
      </div>

      {isFinancialRole && series.length > 0 && (
        <Card className="p-5 md:p-6 rounded-2xl border-slate-200" data-testid="chart-monthly">
          <div className="text-xs uppercase tracking-widest text-[var(--terracotta)] font-semibold">Andamento {year}</div>
          <h3 className="font-display text-xl font-bold text-slate-900 mb-4">Provvigioni PuraVida mese per mese</h3>
          <div style={{ width: "100%", height: 240 }}>
            <ResponsiveContainer minWidth={0} minHeight={240} debounce={50}>
              <BarChart data={series} margin={{ top: 5, right: 12, left: -12, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#64748B" }} />
                <YAxis tick={{ fontSize: 11, fill: "#64748B" }} />
                <Tooltip formatter={(v) => money(v)} contentStyle={{ borderRadius: 12, border: "1px solid #E2E8F0" }} />
                <Bar dataKey="management_fee" name="Provvigioni" fill="#2E5C60" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}
    </div>
  );
}
